import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  Close,
  AccountCircle,
  Info,
  Edit,
  Save,
  Cancel,
  Logout,
  Check,
  Star,
  Upgrade,
} from '@mui/icons-material';
import { Auth } from 'aws-amplify';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, subscriptionAPI, UserProfile } from '../services/apiService';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, updateUser, logout } = useAuth();

  // Safe percentage calculation to avoid division by zero
  const calculateUsagePercentage = (usage: number, limit: number): number => {
    if (!limit || limit <= 0 || !usage || usage < 0) return 0;
    return Math.min((usage / limit) * 100, 100);
  };
  
  const [selectedTab, setSelectedTab] = useState(1);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<'pro' | 'premium'>('pro');
  const [showPlanSelection, setShowPlanSelection] = useState(false);

  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const profile = await userAPI.getProfile();
      setUserProfile(profile);
      
      // Update the auth context with subscription and usage data
      updateUser({
        name: profile.name,
        subscriptionId: profile.subscriptionId,
        subscriptionStatus: profile.subscriptionStatus,
        paymentStatus: profile.paymentStatus,
        planId: profile.planId,
        subscriptionStart: profile.subscriptionStart,
        subscriptionEnd: profile.subscriptionEnd,
        monthlyUsage: profile.monthlyUsage,
        monthlyLimit: profile.monthlyLimit,
        remainingUsage: profile.remainingUsage,
      });
    } catch (err: any) {
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [updateUser]);

  // Load user profile when modal opens (only trigger on open, not user changes)
  useEffect(() => {
    if (open && user && !loading) {
      loadUserProfile();
    }
  }, [open, loadUserProfile]); // Removed 'user' to prevent infinite loop when updateUser is called

  // Update name field when userProfile changes
  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name);
    }
  }, [userProfile]);

  const handleSaveName = async () => {
    if (!name.trim()) {
      setError('Name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await userAPI.updateProfile({ name: name.trim() });
      
      // Update local state
      const updatedProfile = { ...userProfile!, name: name.trim() };
      setUserProfile(updatedProfile);
      updateUser({ name: name.trim() });
      
      setEditingName(false);
      setSuccess('Name updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to update name. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setName(userProfile?.name || '');
    setEditingName(false);
    setError('');
  };

  const handleCreateSubscription = async (plan: 'pro' | 'premium') => {
    try {
      setLoading(true);
      setError('');
      const { url } = await subscriptionAPI.createCheckoutSession(plan);
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (err: any) {
      setError('Failed to create subscription. Please try again.');
      setLoading(false);
    }
  };

  const handleSubscribeClick = () => {
    if (showPlanSelection) {
      handleCreateSubscription(selectedPlan);
    } else {
      setShowPlanSelection(true);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.'
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      setError('');
      await subscriptionAPI.cancelSubscription();
      
      // Refresh user profile to get updated subscription status
      await loadUserProfile();
      
      setSuccess('Subscription canceled successfully. Access will continue until the end of your billing period.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const confirmed = window.confirm('Are you sure you want to log out?');
    if (!confirmed) return;
    
    try {
      await Auth.signOut();
      localStorage.removeItem('aws-token');
      logout();
      onClose();
    } catch (error) {
      setError('Error during logout. Please try again.');
    }
  };

  const getSubscriptionStatusChip = () => {
    if (!userProfile?.subscriptionStatus) {
      return <Chip label="No Subscription" color="default" size="small" />;
    }

    const status = userProfile.subscriptionStatus.toLowerCase();
    switch (status) {
      case 'active':
        return (
          <Chip 
            label="Active" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              color: 'rgba(46, 125, 50, 0.8)',
              fontWeight: 500
            }} 
          />
        );
      case 'canceled':
      case 'cancelled':
        return (
          <Chip 
            label="Canceled" 
            size="small" 
            sx={{ 
              backgroundColor: '#ffebee',
              color: '#c62828',
              fontWeight: 500
            }} 
          />
        );
      case 'trialing':
        return (
          <Chip 
            label="Trial" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(33, 150, 243, 0.15)',
              color: 'rgba(25, 118, 210, 0.8)',
              fontWeight: 500
            }} 
          />
        );
      case 'past_due':
        return <Chip label="Past Due" color="warning" size="small" />;
      case 'unpaid':
        return (
          <Chip 
            label="Payment Failed" 
            size="small" 
            sx={{ 
              backgroundColor: '#ffebee',
              color: '#c62828',
              fontWeight: 500
            }} 
          />
        );
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const getPlanInfo = (plan: 'pro' | 'premium') => {
    if (plan === 'pro') {
      return {
        name: 'Pro Plan',
        price: '$10',
        description: 'Perfect for small teams',
        features: [
          'Up to 15 interviews per month',
          'Real-time monitoring',
          'Basic analytics',
          'Email support',
        ],
      };
    } else {
      return {
        name: 'Premium Plan',
        price: '$25',
        description: 'Best for growing companies',
        features: [
          'Up to 100 interviews per month',
          'Real-time monitoring',
          'Advanced analytics',
          'Priority support',
          'Export reports',
          'Team management',
        ],
      };
    }
  };

  const getCurrentPlanType = (): 'free' | 'pro' | 'premium' => {
    if (!userProfile?.planId) return 'free';
    
    // Use same mapping as backend lambdas/user/handler.go:63-67
    switch (userProfile.planId) {
      case 'price_1Ri3WLHUMdSmzu5CZNA8sOfP': // Pro plan price ID
        return 'pro';
      case 'price_1Ri3XiHUMdSmzu5CnIysiZvA': // Premium plan price ID  
        return 'premium';
      default:
        return 'free';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        '& .MuiDialog-paper': { 
          borderRadius: 3, 
          maxHeight: '90vh' 
        },
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '200px' }, borderRight: { md: '1px solid #e0e0e0' } }}>
          <Typography variant="h6" fontWeight={700} sx={{ p: 2 }}>
            Settings
          </Typography>
          
          {isMobile && <Divider />}
          
          <Box>
            <Box
              sx={{
                p: 1,
                display: 'flex',
                alignItems: 'center',
                color: selectedTab === 1 ? 'primary.main' : 'inherit',
                fontWeight: selectedTab === 1 ? 700 : 400,
                borderLeft: selectedTab === 1 ? '3px solid' : '3px solid transparent',
                borderColor: selectedTab === 1 ? 'primary.main' : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
              onClick={() => setSelectedTab(1)}
            >
              <AccountCircle sx={{ mr: 1 }} />
              <Typography fontWeight={selectedTab === 1 ? 700 : 400}>
                ACCOUNT
              </Typography>
            </Box>
            
            <Box
              sx={{
                p: 1,
                display: 'flex',
                alignItems: 'center',
                color: selectedTab === 2 ? 'primary.main' : 'inherit',
                fontWeight: selectedTab === 2 ? 700 : 400,
                borderLeft: selectedTab === 2 ? '3px solid' : '3px solid transparent',
                borderColor: selectedTab === 2 ? 'primary.main' : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
              onClick={() => setSelectedTab(2)}
            >
              <Info sx={{ mr: 1 }} />
              <Typography fontWeight={selectedTab === 2 ? 700 : 400}>
                ABOUT
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, p: 2 }}>
          <DialogTitle sx={{ p: 0, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>
              {selectedTab === 1 ? 'Account' : 'About'} Settings
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          
          {isMobile && <Divider sx={{ mb: 2 }} />}
          
          <DialogContent sx={{ p: 0, pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {loading && !userProfile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {selectedTab === 1 && (
              <Stack spacing={3}>
                {/* Profile Information */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Profile Information
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Name Field */}
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Name
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {editingName ? (
                          <>
                            <TextField
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              size="small"
                              fullWidth
                              variant="outlined"
                              autoFocus
                              error={!!error && error.includes('Name')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveName();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancelEdit();
                                }
                              }}
                            />
                            <IconButton onClick={handleSaveName} disabled={loading} color="primary">
                              {loading ? <CircularProgress size={20} /> : <Save fontSize="small" />}
                            </IconButton>
                            <IconButton onClick={handleCancelEdit} disabled={loading}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Typography variant="body1" sx={{ flex: 1 }}>
                              {userProfile?.name || 'Not set'}
                            </Typography>
                            <IconButton size="small" onClick={() => setEditingName(true)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Email Field */}
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Email (Read Only)
                      </Typography>
                      <Typography variant="body1">
                        {userProfile?.email || user?.email || 'Not set'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* Subscription Section */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Subscription
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getSubscriptionStatusChip()}
                        {userProfile?.subscriptionStatus === 'active' && (
                          <Chip 
                            label={getPlanInfo(getCurrentPlanType() as 'pro' | 'premium').name}
                            variant="outlined"
                            size="small"
                            icon={<Star />}
                          />
                        )}
                      </Box>
                      
                      {/* Show end date info for canceled subscriptions */}
                      {userProfile?.subscriptionStatus?.toLowerCase() === 'canceled' &&
                       userProfile?.subscriptionEnd &&
                       new Date(userProfile.subscriptionEnd) > new Date() && (
                        <Alert
                          severity="info"
                          sx={{
                            mt: 1,
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                          }}
                        >
                          Your premium access will continue until{' '}
                          {new Date(userProfile.subscriptionEnd).toLocaleDateString()}.
                        </Alert>
                      )}
                    </Box>
                    
                    {/* Usage quota display */}
                    {userProfile && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Monthly Usage
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6" fontWeight={600}>
                            {userProfile.monthlyUsage} / {userProfile.monthlyLimit}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            interviews used
                          </Typography>
                        </Box>
                        
                        {/* Usage progress bar */}
                        <Box sx={{ width: '100%', mb: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={calculateUsagePercentage(userProfile.monthlyUsage, userProfile.monthlyLimit)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: userProfile.remainingUsage <= 5 ? '#f44336' : 
                                                userProfile.remainingUsage <= 10 ? '#ff9800' : '#4caf50'
                              }
                            }}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          {userProfile.remainingUsage} interviews remaining this month
                        </Typography>
                        
                        {/* Warning for low usage */}
                        {userProfile.remainingUsage <= 5 && userProfile.remainingUsage > 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            You're running low on interviews. Consider upgrading your plan.
                          </Alert>
                        )}
                        
                        {/* No usage remaining */}
                        {userProfile.remainingUsage <= 0 && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            You've reached your monthly limit. Upgrade to continue creating interviews.
                          </Alert>
                        )}
                      </Box>
                    )}

                    {/* Current plan info for active subscriptions */}
                    {userProfile?.subscriptionStatus === 'active' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Current Plan
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {getPlanInfo(getCurrentPlanType() as 'pro' | 'premium').name} - {getPlanInfo(getCurrentPlanType() as 'pro' | 'premium').price}/month
                        </Typography>
                        {userProfile?.subscriptionEnd && (
                          <Typography variant="body2" color="text.secondary">
                            Next billing: {new Date(userProfile.subscriptionEnd).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Plan selection for new subscriptions */}
                    {(!userProfile?.subscriptionStatus || 
                      userProfile.subscriptionStatus?.toLowerCase() === 'canceled' ||
                      userProfile.subscriptionStatus?.toLowerCase() === 'incomplete') && (
                      <>
                        {!showPlanSelection ? (
                          <Button
                            variant="contained"
                            onClick={handleSubscribeClick}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <Upgrade />}
                            sx={{
                              backgroundColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.dark'
                              }
                            }}
                          >
                            Choose a Plan
                          </Button>
                        ) : (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Choose your plan
                            </Typography>
                            <RadioGroup
                              value={selectedPlan}
                              onChange={(e) => setSelectedPlan(e.target.value as 'pro' | 'premium')}
                            >
                              <Grid container spacing={2}>
                                {(['pro', 'premium'] as const).map((plan) => {
                                  const planInfo = getPlanInfo(plan);
                                  return (
                                    <Grid item xs={12} sm={6} key={plan}>
                                      <Card 
                                        variant="outlined"
                                        sx={{ 
                                          cursor: 'pointer',
                                          border: selectedPlan === plan ? '2px solid' : '1px solid',
                                          borderColor: selectedPlan === plan ? 'primary.main' : 'divider',
                                          '&:hover': {
                                            borderColor: 'primary.main',
                                          }
                                        }}
                                        onClick={() => setSelectedPlan(plan)}
                                      >
                                        <CardContent sx={{ pb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <FormControlLabel
                                              value={plan}
                                              control={<Radio size="small" />}
                                              label=""
                                              sx={{ m: 0, mr: 1 }}
                                            />
                                            <Typography variant="h6" fontWeight={600}>
                                              {planInfo.name}
                                            </Typography>
                                            {plan === 'premium' && (
                                              <Chip 
                                                label="Popular" 
                                                size="small" 
                                                color="primary" 
                                                sx={{ ml: 1 }}
                                              />
                                            )}
                                          </Box>
                                          <Typography variant="h4" fontWeight={700} color="primary.main">
                                            {planInfo.price}
                                            <Typography 
                                              component="span" 
                                              variant="body2" 
                                              color="text.secondary"
                                            >
                                              /month
                                            </Typography>
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {planInfo.description}
                                          </Typography>
                                          <List dense sx={{ py: 0 }}>
                                            {planInfo.features.map((feature, index) => (
                                              <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                                                <ListItemIcon sx={{ minWidth: 24 }}>
                                                  <Check fontSize="small" color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                  primary={feature} 
                                                  primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                              </ListItem>
                                            ))}
                                          </List>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </RadioGroup>
                            
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Button
                                variant="contained"
                                onClick={handleSubscribeClick}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : null}
                                sx={{ flex: 1 }}
                              >
                                Subscribe to {getPlanInfo(selectedPlan).name}
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={() => setShowPlanSelection(false)}
                                disabled={loading}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                    
                    {/* Cancel subscription button for active users */}
                    {userProfile?.subscriptionStatus === 'active' && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelSubscription}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Logout Section */}
                <Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Logout />}
                    onClick={handleLogout}
                    sx={{ 
                      borderColor: 'text.secondary', 
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'text.primary',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    Log Out
                  </Button>
                </Box>
              </Stack>
            )}

            {selectedTab === 2 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Application Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Version 1.0.0
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    About Interview Zen
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Interview Zen provides comprehensive technical interview solutions with real-time analysis and insights.
                    Our platform helps companies conduct effective technical interviews while providing candidates with
                    a seamless and professional experience.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Questions? Contact us at{' '}
                    <a
                      href="mailto:support@interviewzen.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      support@interviewzen.com
                    </a>
                    .
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    © 2025 Interview Zen. All rights reserved.
                  </Typography>
                </Box>
              </Stack>
            )}
          </DialogContent>
        </Box>
      </Stack>
    </Dialog>
  );
};

export default SettingsModal;